import { useEffect, useState } from 'react'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import TextField from '@mui/material/TextField'
import Button from '@mui/material/Button'
import Box from '@mui/material/Box'
import Alert from '@mui/material/Alert'
import Autocomplete from '@mui/material/Autocomplete'
import InputAdornment from '@mui/material/InputAdornment'
import { api } from '../../../shared/services/API'
import { getBackendError } from '../../../shared/utils/formErrors'
import type { Page } from '../../../shared/utils/types'
import { CICLO_LABEL, formatMoney, type Subscricao } from '../subscricoesModel'

interface Props {
    open: boolean
    onClose: () => void
    onSaved: () => void
}

export default function CobrancaFormDialog({ open, onClose, onSaved }: Props) {
    const [subscricoes, setSubscricoes] = useState<Subscricao[]>([])
    const [subscricao, setSubscricao] = useState<Subscricao | null>(null)
    const [periodoInicio, setPeriodoInicio] = useState('')
    const [periodoFim, setPeriodoFim] = useState('')
    const [valor, setValor] = useState('')
    const [vencimentoEm, setVencimentoEm] = useState('')
    const [apiError, setApiError] = useState('')
    const [submitting, setSubmitting] = useState(false)

    useEffect(() => {
        if (!open) return
        // eslint-disable-next-line react-hooks/set-state-in-effect -- reset dos campos quando o diálogo abre
        setApiError('')
        setSubscricao(null)
        setPeriodoInicio('')
        setPeriodoFim('')
        setValor('')
        setVencimentoEm('')
        api.get<Page<Subscricao>>('/api/admin/subscricoes', { params: { size: 200, estado: 'ACTIVA', sort: 'createdAt,desc' } })
            .then((r) => setSubscricoes(r.data.content ?? []))
            .catch(() => setApiError('Não foi possível carregar as subscrições.'))
    }, [open])

    const submit = async () => {
        if (!subscricao) { setApiError('Escolha a subscrição.'); return }
        if (!periodoInicio || !periodoFim) { setApiError('Indique o período.'); return }
        const valorNum = Number(valor)
        if (valor.trim() === '' || Number.isNaN(valorNum) || valorNum < 0) { setApiError('Valor inválido.'); return }

        setSubmitting(true)
        setApiError('')
        try {
            await api.post('/api/admin/subscricoes/cobrancas', {
                subscricaoId: subscricao.id,
                periodoInicio,
                periodoFim,
                valor: valorNum,
                vencimentoEm: vencimentoEm || null,
            })
            onSaved()
        } catch (err) {
            setApiError(getBackendError(err)?.message ?? 'Não foi possível criar a cobrança.')
        } finally {
            setSubmitting(false)
        }
    }

    return (
        <Dialog open={open} onClose={submitting ? undefined : onClose} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ fontWeight: 700 }}>Nova cobrança</DialogTitle>
            <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                {apiError && <Alert severity="error" sx={{ borderRadius: 2 }}>{apiError}</Alert>}

                <Autocomplete
                    options={subscricoes}
                    value={subscricao}
                    onChange={(_, v) => {
                        setSubscricao(v)
                        if (v && valor.trim() === '') setValor(String(v.preco))
                    }}
                    getOptionLabel={(s) => `${s.tenantNome ?? s.tenantId} — ${s.planoNome ?? ''} (${CICLO_LABEL[s.cicloFaturacao]})`}
                    renderInput={(params) => <TextField {...params} label="Subscrição" />}
                    isOptionEqualToValue={(a, b) => a.id === b.id}
                />

                <Box sx={{ display: 'flex', gap: 2 }}>
                    <TextField
                        label="Início do período"
                        type="date"
                        value={periodoInicio}
                        onChange={(e) => setPeriodoInicio(e.target.value)}
                        slotProps={{ inputLabel: { shrink: true } }}
                        sx={{ flex: 1 }}
                    />
                    <TextField
                        label="Fim do período"
                        type="date"
                        value={periodoFim}
                        onChange={(e) => setPeriodoFim(e.target.value)}
                        slotProps={{ inputLabel: { shrink: true } }}
                        sx={{ flex: 1 }}
                    />
                </Box>

                <Box sx={{ display: 'flex', gap: 2 }}>
                    <TextField
                        label="Valor"
                        type="number"
                        value={valor}
                        onChange={(e) => setValor(e.target.value)}
                        helperText={subscricao ? `Preço da subscrição: ${formatMoney(subscricao.preco)}` : ' '}
                        slotProps={{
                            input: { endAdornment: <InputAdornment position="end">MT</InputAdornment> },
                            htmlInput: { min: 0, step: '0.01' },
                        }}
                        sx={{ flex: 1 }}
                    />
                    <TextField
                        label="Vencimento"
                        type="date"
                        value={vencimentoEm}
                        onChange={(e) => setVencimentoEm(e.target.value)}
                        helperText="Vazio = emissão + prazo padrão"
                        slotProps={{ inputLabel: { shrink: true } }}
                        sx={{ flex: 1 }}
                    />
                </Box>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={onClose} disabled={submitting}>Cancelar</Button>
                <Button variant="contained" onClick={submit} disabled={submitting}>
                    {submitting ? 'A criar…' : 'Criar cobrança'}
                </Button>
            </DialogActions>
        </Dialog>
    )
}
